import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAppContext, Driver } from '@/contexts/AppContext';
import { toast } from '@/components/ui/use-toast';
import { normalizePhoneInput } from '@/lib/phone';

interface DriverContactFormProps {
  driver: Driver;
  onSave: () => void;
}

const DriverContactForm: React.FC<DriverContactFormProps> = ({ driver, onSave }) => {
  const { updateDriver } = useAppContext();
  const [email, setEmail] = useState(driver.email || '');
  const [phone, setPhone] = useState(driver.phone || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim() || !phone.trim()) {
      toast({
        title: "Missing Information",
        description: "Please provide both email and phone number.",
        variant: "destructive"
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }

    updateDriver(driver.id, { email: email.trim(), phone: phone.trim() });
    toast({
      title: "Contact Information Updated",
      description: "Your contact details have been saved."
    });
    onSave();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <CardTitle className="text-xl font-bold text-gray-800">
            Update Contact Information
          </CardTitle>
          <p className="text-sm text-gray-600 mt-2">
            Please provide your email and phone number for payment summaries
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(normalizePhoneInput(e.target.value))}
                placeholder="+61 400 123 456"
                required
              />
            </div>

            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
              Save Contact Information
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default DriverContactForm;